import { useState, useCallback, useRef, useEffect } from 'react'
import { FaceMesh, Results } from '@mediapipe/face_mesh'

export interface FaceLandmarks {
  leftEye: { x: number; y: number }
  rightEye: { x: number; y: number }
  noseTip: { x: number; y: number }
  faceWidth: number
  faceAngle: number
}

interface UseFaceDetectionReturn {
  landmarks: FaceLandmarks | null
  isDetecting: boolean
  isModelLoaded: boolean
  error: string | null
  processFrame: (video: HTMLVideoElement) => Promise<void>
}

// MediaPipe Face Mesh landmark indices
const LEFT_EYE_CENTER = 468 // Left iris center
const RIGHT_EYE_CENTER = 473 // Right iris center
const NOSE_TIP = 1
const LEFT_FACE_EDGE = 234
const RIGHT_FACE_EDGE = 454

export function useFaceDetection(): UseFaceDetectionReturn {
  const [landmarks, setLandmarks] = useState<FaceLandmarks | null>(null)
  const [isDetecting, setIsDetecting] = useState(false)
  const [isModelLoaded, setIsModelLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const faceMeshRef = useRef<FaceMesh | null>(null)
  const isInitializingRef = useRef(false)

  const initFaceMesh = async () => {
    if (faceMeshRef.current || isInitializingRef.current) return

    isInitializingRef.current = true
    try {
      const faceMesh = new FaceMesh({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      })

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      })

      faceMesh.onResults((results: Results) => {
        if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
          const face = results.multiFaceLandmarks[0]

          const leftEye = face[LEFT_EYE_CENTER]
          const rightEye = face[RIGHT_EYE_CENTER]
          const noseTip = face[NOSE_TIP]
          const leftEdge = face[LEFT_FACE_EDGE]
          const rightEdge = face[RIGHT_FACE_EDGE]

          const faceWidth = Math.sqrt(
            Math.pow(rightEdge.x - leftEdge.x, 2) +
            Math.pow(rightEdge.y - leftEdge.y, 2)
          )

          const deltaY = rightEye.y - leftEye.y
          const deltaX = rightEye.x - leftEye.x
          const faceAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI)

          setLandmarks({
            leftEye: { x: leftEye.x, y: leftEye.y },
            rightEye: { x: rightEye.x, y: rightEye.y },
            noseTip: { x: noseTip.x, y: noseTip.y },
            faceWidth,
            faceAngle,
          })
        } else {
          setLandmarks(null)
        }

        setIsDetecting(false)
      })

      await faceMesh.initialize()
      faceMeshRef.current = faceMesh
      setIsModelLoaded(true)
    } catch (err) {
      setError('Error al cargar el modelo de detección facial')
      console.error('FaceMesh initialization error:', err)
    } finally {
      isInitializingRef.current = false
    }
  }

  useEffect(() => {
    return () => {
      if (faceMeshRef.current) {
        faceMeshRef.current.close()
        faceMeshRef.current = null
      }
    }
  }, [])

  const processFrame = useCallback(async (video: HTMLVideoElement) => {
    // If not initialized, trigger initialization
    if (!faceMeshRef.current) {
      await initFaceMesh()
    }

    // If it was already initializing, initFaceMesh returns immediately, 
    // but the model might not be ready yet. Wait for isModelLoaded if we have a faceMesh.
    if (!faceMeshRef.current || isInitializingRef.current) return

    setIsDetecting(true)

    try {
      await faceMeshRef.current.send({ image: video })
    } catch (err) {
      console.error('Frame processing error:', err)
      setIsDetecting(false)
    }
  }, []) // Removed isModelLoaded dependency to prevent unnecessary re-creations during init

  return {
    landmarks,
    isDetecting,
    isModelLoaded,
    error,
    processFrame,
  }
}
